/**
 * The provisional document on the invoice and till screens (arrêté
 * n°2025-0049, art. 6 g)):
 * - the « DOCUMENT PROVISOIRE » badge of a document issued while no MCF could
 *   certify the sale;
 * - the status badge of its regularisation;
 * - on a sale not issued yet, the panel that issues it as a provisional
 *   document when the server says that request would be accepted, or says
 *   why it would not, in the server's own words.
 */

import { AlertTriangle, FileClock } from 'lucide-react';
import type { ProvisionalOfferWire, RegularisationStatusWire } from '@efacture/fiscal-core';

import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useTranslation, type TranslationKey } from '../../lib/billing-i18n';

const SIZE_STYLES = {
  sm: { badge: 'gap-1 px-2.5 py-0.5 text-xs', icon: 'h-3 w-3' },
  md: { badge: 'gap-1.5 px-3 py-1 text-sm', icon: 'h-4 w-4' },
} as const;

const STATUS_LABEL_KEYS: Record<RegularisationStatusWire, TranslationKey> = {
  A_REGULARISER: 'provisional.status.A_REGULARISER',
  EN_RETARD: 'provisional.status.EN_RETARD',
  REGULARISEE: 'provisional.status.REGULARISEE',
  REGULARISEE_HORS_DELAI: 'provisional.status.REGULARISEE_HORS_DELAI',
};

const STATUS_STYLES: Record<RegularisationStatusWire, string> = {
  A_REGULARISER: 'border-amber-200 bg-amber-100 text-amber-900',
  EN_RETARD: 'border-red-200 bg-red-100 text-red-800',
  REGULARISEE: 'border-emerald-200 bg-emerald-100 text-emerald-800',
  REGULARISEE_HORS_DELAI: 'border-orange-200 bg-orange-100 text-orange-800',
};

/** jj/mm/aaaa à hh:mm of an instant (Africa/Ouagadougou is UTC). */
export function formatProvisionalInstant(value: string): string {
  const iso = new Date(value).toISOString();
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} à ${iso.slice(11, 16)}`;
}

/** Whether an invoice row is a provisional document (`ISSUANCE_REGIMES.PROVISIONAL`). */
export function isProvisionalDocument(invoice: { issuanceRegime?: string | null | undefined }): boolean {
  return invoice.issuanceRegime === 'PROVISIONAL';
}

export function ProvisionalBadge({ size = 'md' }: { size?: keyof typeof SIZE_STYLES }) {
  const { t } = useTranslation();
  const sizing = SIZE_STYLES[size];
  return (
    <span
      data-testid="provisional-badge"
      className={cn(
        'inline-flex items-center rounded-full border border-amber-300 bg-amber-100 font-medium text-amber-900',
        sizing.badge
      )}
    >
      <FileClock className={sizing.icon} aria-hidden="true" />
      {t('provisional.badge')}
    </span>
  );
}

export function RegularisationStatusBadge({
  status,
  size = 'md',
}: {
  status: RegularisationStatusWire;
  size?: keyof typeof SIZE_STYLES;
}) {
  const { t } = useTranslation();
  const sizing = SIZE_STYLES[size];
  return (
    <span
      data-testid="regularisation-status"
      data-status={status}
      className={cn('inline-flex items-center rounded-full border font-medium', sizing.badge, STATUS_STYLES[status])}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}

interface ProvisionalIssuePanelProps {
  /** `provisionalOffer` of the invoice or of the sale; the panel shows nothing without an open outage. */
  offer: ProvisionalOfferWire | null | undefined;
  isIssuing: boolean;
  onIssue: () => void;
  /** Text above the action whatever the viewport, for a narrow container such as a dialog. */
  stacked?: boolean;
}

export function ProvisionalIssuePanel({ offer, isIssuing, onIssue, stacked = false }: ProvisionalIssuePanelProps) {
  const { t } = useTranslation();
  if (!offer || offer.outageId === null) return null;
  const refused = !offer.accepted;
  const icon = <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />;
  const message = (
    <div className="flex-1 text-sm text-amber-900">
      {refused ? (
        <p data-testid="provisional-refusal">
          <span className="font-medium">{t('provisional.issue.refused')}</span> {offer.refusal?.message}
        </p>
      ) : (
        <p>{t('provisional.issue.panel')}</p>
      )}
    </div>
  );
  return (
    <div
      role="status"
      data-testid="provisional-issue-panel"
      data-accepted={offer.accepted}
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4',
        stacked ? 'mb-4' : 'mb-6 sm:flex-row sm:items-center'
      )}
    >
      {stacked ? (
        <div className="flex items-start gap-3">
          {icon}
          {message}
        </div>
      ) : (
        <>
          {icon}
          {message}
        </>
      )}
      {refused ? null : (
        <Button
          onClick={onIssue}
          disabled={isIssuing}
          variant="outline"
          className="border-amber-400 text-amber-900 hover:bg-amber-100"
        >
          {t('provisional.issue.action')}
        </Button>
      )}
    </div>
  );
}
